/**
 * Replace a membership plan through ant's `replaceMembership` mutation.
 *
 * Moves every live rider on plan A to plan B (same system), then archives A
 * pointing at B. `dryRun` defaults to true on the server, and this hook always
 * sends it explicitly, so a review step can never write by accident.
 *
 * Unlike the Ash CRUD mutations (which return `{ result, errors }`), this one
 * returns the result directly and refuses with top-level GraphQL errors that
 * carry a stable `code` and the ids behind the refusal in `vars`. Because the
 * result field is non-null, a refusal nulls `data` and Relay reports it through
 * `onError`, with the server's errors on `error.source.errors`.
 *
 * v1 defaults decided for Julio (he did not answer them; see the ant docs in
 * docs/membership-replace.md): Movatic admins only, canceled-but-paid riders
 * whose term has not ended move, usage carries over, and riders are not
 * notified.
 *
 * Note: Run `npm run relay` after changing the mutation text.
 */

import { useCallback } from 'react';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import type {
  useReplaceMembershipMutation as ReplaceMembershipMutationType,
  useReplaceMembershipMutation$data,
} from './__generated__/useReplaceMembershipMutation.graphql';

const ReplaceMembershipMutation = graphql`
  mutation useReplaceMembershipMutation($input: ReplaceMembershipInput!) {
    replaceMembership(input: $input) {
      status
      dryRun
      membershipId
      replacementMembershipId
      replacedOn
      movedCount
      free {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      paidSameInterval {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      pending {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      canceledButPaid {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      collisions {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      withUsage {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      emailDomainExceptions {
        count
        samples {
          userMembershipId
          userId
          userName
          email
          fromPriceId
          toPriceId
          oldAmount
          newAmount
          nextChargeOn
          paidUntil
          collidesWithUserMembershipId
        }
      }
      perksToArchive
      pricesToArchive
      qrCodeCount
      qrCodeIds
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type MembershipReplaceResult =
  useReplaceMembershipMutation$data['replaceMembership'];
export type MembershipReplaceBucket = MembershipReplaceResult['free'];
export type MembershipReplaceSample = MembershipReplaceBucket['samples'][number];

/** Refusal codes ant returns, plus `forbidden` from the policy. */
export type ReplaceRefusalCode =
  | 'membership_not_found'
  | 'replacement_not_found'
  | 'same_membership'
  | 'different_system'
  | 'replacement_archived'
  | 'membership_archived'
  | 'go_membership'
  | 'invalid_price_map'
  | 'unmapped_price'
  | 'interval_mismatch'
  | 'fixed_term_not_covered'
  | 'forbidden';

export interface ReplaceMembershipError {
  code: string | null;
  message: string;
  /** Ids behind the refusal, e.g. `{ price_ids: [...] }`. */
  vars: Record<string, unknown>;
}

export interface PriceMapping {
  fromPriceId: string;
  toPriceId: string;
}

export interface ReplaceMembershipInput {
  membershipId: string;
  replacementMembershipId: string;
  priceMap: PriceMapping[];
  dryRun: boolean;
  reason?: string;
}

export interface ReplaceMembershipOutcome {
  data: MembershipReplaceResult | null;
  errors: ReplaceMembershipError[];
  isSuccess: boolean;
}

// ============================================================================
// Error normalization
// ============================================================================

interface RawGraphQLError {
  message?: string;
  code?: string;
  vars?: unknown;
  extensions?: { code?: string; vars?: unknown };
}

function toRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * ant (Absinthe) puts `code` and `vars` at the top level of each error; other
 * servers put them under `extensions`. Both are read.
 */
export function normalizeReplaceErrors(raw: unknown): ReplaceMembershipError[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: RawGraphQLError) => ({
    code: item?.code ?? item?.extensions?.code ?? null,
    message: item?.message ?? 'The request failed.',
    vars: toRecord(item?.vars ?? item?.extensions?.vars),
  }));
}

/** Pulls the server's errors out of the Error Relay hands to `onError`. */
export function errorsFromRelayError(error: Error): ReplaceMembershipError[] {
  const source = (error as Error & { source?: { errors?: unknown } }).source;
  const fromSource = normalizeReplaceErrors(source?.errors);
  if (fromSource.length > 0) return fromSource;
  return [{ code: null, message: error.message, vars: {} }];
}

// ============================================================================
// Hook
// ============================================================================

/**
 * @example
 * const { replaceMembership, isLoading } = useReplaceMembershipMutation();
 * const review = await replaceMembership({
 *   membershipId: a.id,
 *   replacementMembershipId: b.id,
 *   priceMap: [{ fromPriceId: aPrice.id, toPriceId: bPrice.id }],
 *   dryRun: true,
 * });
 */
export function useReplaceMembershipMutation() {
  const [commit, isInFlight] = useMutation<ReplaceMembershipMutationType>(
    ReplaceMembershipMutation
  );

  const replaceMembership = useCallback(
    (input: ReplaceMembershipInput): Promise<ReplaceMembershipOutcome> => {
      return new Promise((resolve) => {
        commit({
          variables: {
            input: {
              membershipId: input.membershipId,
              replacementMembershipId: input.replacementMembershipId,
              priceMap: input.priceMap,
              dryRun: input.dryRun,
              ...(input.reason ? { reason: input.reason } : {}),
            },
          },
          onCompleted: (response: useReplaceMembershipMutation$data, errors: unknown) => {
            const normalized = normalizeReplaceErrors(errors);
            const data = response?.replaceMembership ?? null;
            resolve({
              data,
              errors: normalized,
              isSuccess: normalized.length === 0 && data !== null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: errorsFromRelayError(error),
              isSuccess: false,
            });
          },
        });
      });
    },
    [commit]
  );

  return { replaceMembership, isLoading: isInFlight };
}
